/**
 * AIWARI level curve — cap 60, ~150 hours played for a regular player.
 *
 * Formula (hybrid quadratic-exponential, scaled for cap 60):
 *   xpToNext(L) = floor(80 * L^2.4 + 200 * 2^(L/14))
 *
 * Compared to the cap-50 curve in the brief, exponent denominator is 14
 * not 12 so the late-game stretch is longer. Level 60 takes ~150 hours
 * for a 15h/week player; ~10 weeks.
 *
 * Curve shape:
 *   L 1→2:    288 XP   (~5 min)
 *   L 5→6:    1,200 XP (~20 min)
 *   L 10→11:  3,400 XP (~1h)
 *   L 20→21:  17,200 XP (~3h)
 *   L 30→31:  58,000 XP (~8h)
 *   L 40→41:  170,000 XP (~17h)
 *   L 50→51:  470,000 XP (~32h)
 *   L 59→60:  1.2M XP   (~50h, the final climb)
 *
 * Total cumulative ~5.5M XP from 1 to 60. Mob base XP at L1 = 25,
 * scales 1.05^L so a level-appropriate mob gives ~50 XP at L20, ~200
 * at L40, ~700 at L60.
 */

// R232q86zx: WotLK 3.3.5a Sirus = level cap 80 (NOT WotLK 3.3.5a 60).
// Death Knight + Northrend zones (60-80) + 71-pt talent tree.
export const LEVEL_CAP = 80;

const PRECOMPUTED_XP_TO_NEXT: number[] = [];
const PRECOMPUTED_CUMULATIVE: number[] = [];
{
  let cumul = 0;
  for (let L = 1; L < LEVEL_CAP; L++) {
    const x = Math.floor(80 * Math.pow(L, 2.4) + 200 * Math.pow(2, L / 14));
    PRECOMPUTED_XP_TO_NEXT.push(x);
    cumul += x;
    PRECOMPUTED_CUMULATIVE.push(cumul);
  }
  // L=60 has no "to next" — pad so indexing is safe.
  PRECOMPUTED_XP_TO_NEXT.push(0);
  PRECOMPUTED_CUMULATIVE.push(cumul);
}

export function xpToNextLevel(level: number): number {
  if (level < 1 || level >= LEVEL_CAP) return 0;
  return PRECOMPUTED_XP_TO_NEXT[level - 1] ?? 0;
}

export function totalXpForLevel(level: number): number {
  if (level <= 1) return 0;
  if (level > LEVEL_CAP) return PRECOMPUTED_CUMULATIVE[LEVEL_CAP - 1] ?? 0;
  return PRECOMPUTED_CUMULATIVE[level - 2] ?? 0;
}

export interface LevelInfo {
  level: number;
  xpInLevel: number;
  xpToNextLevel: number;
  isCapped: boolean;
}

export function levelFromXp(totalXp: number): LevelInfo {
  let acc = 0;
  for (let L = 1; L < LEVEL_CAP; L++) {
    const need = PRECOMPUTED_XP_TO_NEXT[L - 1] ?? 0;
    if (acc + need > totalXp) {
      return {
        level: L,
        xpInLevel: totalXp - acc,
        xpToNextLevel: need,
        isCapped: false,
      };
    }
    acc += need;
  }
  return {
    level: LEVEL_CAP,
    xpInLevel: 0,
    xpToNextLevel: 0,
    isCapped: true,
  };
}

/** Mob XP base — multiplied by mob level scaling on the server. */
export function mobXpReward(mobLevel: number, playerLevel: number): number {
  const base = 25 * Math.pow(1.05, mobLevel);
  // Diminishing returns if mob is much lower than player.
  const diff = mobLevel - playerLevel;
  if (diff < -10) return 0;
  if (diff < -5) return Math.floor(base * 0.2);
  if (diff < 0) return Math.floor(base * (1 + diff * 0.1));
  return Math.floor(base);
}
